// Basic
import PropTypes from 'prop-types';
// Store & actions
import actionsUsers from '../../../api/actionsUsers';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
// Common
import ICONS from '../../../../../../../common-utils/enums/icons';
import Permissions from '../../../../../../permissions/Permissions';
import USER_STATUS from '../../../enums/UserStatus';
import userFields from '../../../fields/userFields';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsUser } from '@/app/i18n/namespaces';

/**
 *
 * @param user
 * @param permissions
 * @param setLaunchRefetch
 * @returns {JSX} ActivateDeactivate
 */
function ActivateDeactivate(user, permissions, setLaunchRefetch) {

    // Toast
    const { toast } = useToast();

    // API Calls
    const onSuccessActivateUser = () => {
        toast({
            description: <Entity entity={'toast.success.activate'} namespace={nsUser} />,
            variant: 'success'
        });
        setLaunchRefetch(true);
    };
    const onErrorActivateUser = () => {
        toast({
            description: <Entity entity={'toast.error.activate'} namespace={nsUser} />,
            variant: 'danger'
        });
    };
    const submitActivateUser = actionsUsers.activateUser(onSuccessActivateUser, onErrorActivateUser);

    const onSuccessDeactivateUser = () => {
        toast({
            description: <Entity entity={'toast.success.deactivate'} namespace={nsUser} />,
            variant: 'success'
        });
        setLaunchRefetch(true);
    };
    const onErrorDeactivateUser = () => {
        toast({
            description: <Entity entity={'toast.error.deactivate'} namespace={nsUser} />,
            variant: 'danger'
        });
    };
    const submitDeactivateUser = actionsUsers.deactivateUser(onSuccessDeactivateUser, onErrorDeactivateUser);

    // Other functions
    const activateUser = () => {
        submitActivateUser.mutate(user.uuid);
        toast({
            description: <Entity entity={'toast.loading'} />
        });
    };
    const deactivateUser = () => {
        submitDeactivateUser.mutate(user.uuid);
        toast({
            description: <Entity entity={'toast.loading'} />
        });
    };
    const userStatusIsOneOf = (statuses) => {
        // Abort the case where the user is not loaded yet or where the statuses are invalid
        if (!user || !statuses) {
            return false;
        }

        const userStatus = user[userFields.STATUS.key];
        return statuses.some(status => userStatus === status.name);
    };

    // Render
    return (
        [{
            icon: ICONS.USER_ACTIVATE,
            entity: 'actions.row.activate',
            namespace: nsUser,
            className: 'text-success',
            onClick: activateUser,
            visible: userStatusIsOneOf([USER_STATUS.INACTIVE]) &&
                !!activateUser,
            disabled: !hasRequiredPermissions({ permissions, requiredPermissions: [Permissions.USER_ACTIVATE.name] }),
            tooltipForbidden: 'title.permissions.access_denied'

        }, {
            icon: ICONS.USER_DEACTIVATE,
            entity: 'actions.row.deactivate',
            namespace: nsUser,
            className: 'text-warning',
            onClick: deactivateUser,
            visible: userStatusIsOneOf([USER_STATUS.ACTIVE]) && !!deactivateUser,
            disabled: !hasRequiredPermissions({ permissions, requiredPermissions: [Permissions.USER_DEACTIVATE.name] }),
            tooltipForbidden: 'title.permissions.access_denied'
        }
        ]);
}
ActivateDeactivate.propTypes = {
    user: PropTypes.object.isRequired,
    permissions: PropTypes.array.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default ActivateDeactivate;